import { toast } from 'react-toastify';

import parse from 'date-fns/parse';
import { formatDate } from 'helpers/date';
import * as Rx from 'rxjs';
import { of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import type { IFilter } from 'v2/components/Pages/Reports/Chargeback/Filter/initialValues';
import { initialValues } from 'v2/components/Pages/Reports/Chargeback/Filter/initialValues';
import type { IChargebackFiles, IChargebackList } from 'v2/interfaces/chargeback';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import type { ApiService } from '../api';
import apiService from '../api';
import betaApi from './betaApi';
import legacyApi from './legacyApi';

class ChargebackService {
  private loading$ = new Rx.BehaviorSubject<boolean>(false);
  private loadingFiles$ = new Rx.BehaviorSubject<boolean>(false);
  private filter$ = new Rx.BehaviorSubject<IFilter>(initialValues);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>({ current_page: 1, per_page: 50 });
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);
  private chargebacks$ = new Rx.BehaviorSubject<IChargebackList[]>([]);
  private files$ = new Rx.BehaviorSubject<IChargebackFiles[]>([]);

  private totalItems$ = new Rx.BehaviorSubject<number>(0);
  private totalChargeback$ = new Rx.BehaviorSubject<number>(0);

  constructor(private apiService: ApiService) {}
  public getFilter = (): Rx.Observable<IFilter> => this.filter$;

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public getChargebacks = (): Rx.Observable<IChargebackList[]> => this.chargebacks$;
  public getFiles = (): Rx.Observable<IChargebackFiles[]> => this.files$;

  public getTotalItems = (): Rx.Observable<number> => this.totalItems$;
  public getTotalChargeback = (): Rx.Observable<number> => this.totalChargeback$;

  public isLoading = () => this.loading$;
  public isLoadingFiles = () => this.loadingFiles$;

  public correctFilter = (filter: IFilter) => {
    return {
      requested_at_initial: formatDate(parse(filter.date[0], 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd'),
      requested_at_final: formatDate(parse(filter.date[1], 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd'),
      ...filter,
      ...{ date: null }
    };
  };

  public setFilter(filter: IFilter): void {
    this.filter$.pipe(take(1)).subscribe((currentFilter: IFilter) => {
      this.filter$.next({ ...currentFilter, ...filter });
    });
  }

  public resetPage() {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });

    return this.getPaginationParams();
  }

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next({
      current_page: pagination.current_page,
      per_page: pagination.per_page
    });
  }

  public removeFileFromList(fileId: number) {
    const removedFiles = this.files$.value.filter(item => item.id !== fileId);
    this.files$.next(removedFiles);
    return Rx.empty();
  }

  public refreshChargebackFiles(fileId: number) {
    const removedFiles = this.files$.value.filter(item => item.id !== fileId);
    this.files$.next(removedFiles);
    return Rx.empty();
  }

  public removeChargebackFile(invoiceId: number, fileId: number) {
    return this.getApi().removeChargebackFile(invoiceId, fileId);
  }

  public loadChargebacksFiles(invoiceId: number) {
    this.loadingFiles$.next(true);
    return this.getApi()
      .loadChargebacksFiles(invoiceId)
      .pipe(
        tap(files => this.files$.next(files.data)),
        tap(() => this.loadingFiles$.next(false))
      );
  }

  public uploadChargebackFile(file: File, invoiceId: number) {
    const formData = new FormData();
    formData.append('invoice_id', invoiceId.toString());

    formData.append('files[]', file);

    return this.getApi().uploadChargebackFile(file, invoiceId);
  }

  public loadChargebacks() {
    return this.getFilter().pipe(
      map(f => this.correctFilter(f)),
      switchMap(filter => this.resetPage().pipe(map(params => ({ ...filter, ...params })))),
      tap(() => {
        this.loading$.next(true);
        this.chargebacks$.next([]);
      }),
      debounceTime(10),
      switchMap(filter => this.apiService.get('reports/chargebacks', filter)),
      tap(chargebacks => this.chargebacks$.next([...(chargebacks.data ?? [])])),
      tap(chargebacks => this.paginationResponse$.next(chargebacks.pagination)),
      tap(chargebacks => this.totalItems$.next(chargebacks?.pagination?.total_items || 0)),
      tap(chargebacks => this.totalChargeback$.next(chargebacks?.total_chargeback || 0)),
      tap(() => this.loading$.next(false)),
      catchError(() => {
        toast.error('Ops, ocorreu um erro na listagem de chargebacks.');
        this.chargebacks$.next([]);
        this.paginationResponse$.next(null);
        this.totalItems$.next(0);
        this.loading$.next(false);
        return of(true);
      })
    );
  }

  public cleanFilter() {
    this.filter$.next(initialValues);
  }

  public export() {
    return this.apiService.get('reports/chargebacks', { ...this.correctFilter(this.filter$.value), export: true });
  }

  private getApi() {
    return eduzzAccountsService.verifyUserHasBetaModule('new_chargeback_flow') ? betaApi : legacyApi;
  }
}

const chargebackService = new ChargebackService(apiService);
export default chargebackService;
